/**
 * Coerce store `formImages` from API (string, double-encoded JSON, object) to string paths.
 */
export function parseFormImageList(raw) {
  let v = raw;
  if (v == null || v === "") return [];
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch (_err) {
      return v.trim() ? [v.trim()] : [];
    }
    if (typeof v === "string") {
      try {
        v = JSON.parse(v);
      } catch (_err2) {
        return v.trim() ? [v.trim()] : [];
      }
    }
  }
  if (!Array.isArray(v)) {
    if (v && typeof v === "object") return parseFormImageList(Object.values(v));
    return [];
  }
  return v
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") {
        if (typeof item.path === "string") return item.path.trim();
        if (typeof item.url === "string") return item.url.trim();
      }
      return "";
    })
    .filter(Boolean);
}

/**
 * Build absolute URL for static files served by the API (Images/, etc.).
 * Normalizes Windows-style paths and bare `Store/...` legacy paths.
 */
export function apiAssetUrl(baseUrl, relativePath) {
  if (relativePath == null || relativePath === "") return "";
  const s = String(relativePath).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const base = (baseUrl || "").replace(/\/+$/, "");
  let rel = s.replace(/\\/g, "/").replace(/^\/+/, "");
  const lower = rel.toLowerCase();
  if (!lower.startsWith("images/") && /^store\//i.test(rel)) {
    rel = `Images/${rel}`;
  }
  if (!base) return "";
  return `${base}/${rel}`;
}

/** When REACT_APP_URL is unset, use this public API (avoids surprise localhost in `npm start`). */
const PUBLIC_API_FALLBACK = "https://filmlocation.100billion.in";

/**
 * API origin for fetch() and image URLs.
 * 1. REACT_APP_USE_LOCAL_API=true → http://localhost:8000 (wins over REACT_APP_URL so `npm start` can use a local Backend).
 * 2. REACT_APP_URL when set (strip trailing slashes).
 * 3. Production build in browser: same-origin (only if API is served from the site origin).
 * 4. Development on localhost → http://localhost:8000 (avoids POSTing to CRA :3000 and getting HTML / "Cannot POST").
 * 5. Otherwise PUBLIC_API_FALLBACK.
 */
export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_USE_LOCAL_API === "true") {
    return "http://localhost:8000";
  }
  const trimmed = (process.env.REACT_APP_URL || "").trim().replace(/\/+$/, "");
  if (trimmed) return trimmed;
  if (
    process.env.NODE_ENV !== "development" &&
    typeof window !== "undefined" &&
    window.location &&
    window.location.origin
  ) {
    return window.location.origin;
  }
  if (
    typeof window !== "undefined" &&
    /^localhost$|^127\.0\.0\.1$/i.test(window.location.hostname || "")
  ) {
    return "http://localhost:8000";
  }
  return PUBLIC_API_FALLBACK;
};

/**
 * Base URL for location-permission API (create/upload/update).
 * Backend mounts the same router at /permission and /api/permission.
 * Default is /api/permission — on many hosts /permission/* is handled by static/SPA (404 HTML),
 * while /api/* is proxied to Node. Override: REACT_APP_PERMISSION_API_ROOT=/permission
 */
export const getPermissionApiBase = () => {
  const host = getApiBaseUrl().replace(/\/+$/, "");
  let root = (process.env.REACT_APP_PERMISSION_API_ROOT || "/api/permission").trim();
  if (!root.startsWith("/")) root = `/${root}`;
  root = root.replace(/\/+$/, "") || "/api/permission";
  return `${host}${root}`;
};

/** Parse fetch response as JSON; clear error if server returned HTML (wrong host / 404 SPA). */
export async function fetchApiJson(url, options) {
  const res = await fetch(url, options);
  const text = await res.text();
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    const hint =
      text.trimStart().startsWith("<!") || text.includes("<!DOCTYPE")
        ? " — received HTML (wrong API URL or route not deployed on server)."
        : "";
    throw new Error(
      `Not JSON (${res.status})${hint} Request: ${url.slice(0, 80)}`
    );
  }
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON from ${url.slice(0, 60)}…`);
  }
  return { res, data };
}
